import { parseCoordinateInput, MEDIA_UNIT_COST_FIELD_KEYS } from '@ooh/contracts';
import type { MediaUnitCostFieldKey } from '@ooh/contracts';
import type { FormValues } from '../../../lib/media-unit-form-schema';

/**
 * Returns the raw value if it is a member of the allowed set, otherwise ''.
 * Handles null/undefined/legacy string values that exist in the DB but are no
 * longer in the current dictionary (e.g. "led" was removed from LIGHTING_VALUES).
 * Using `??` alone keeps truthy legacy strings in form state and causes the Zod
 * schema (z.enum(…).or(z.literal(''))) to reject the value on submit.
 */
export function toValidDictValue<T extends readonly string[]>(
  allowed: T,
  raw: string | null | undefined,
): T[number] | '' {
  if (raw != null && (allowed as readonly string[]).includes(raw)) {
    return raw as T[number];
  }
  return '';
}

export function computedArea(widthM: string, heightM: string): string {
  if (!widthM || !heightM) return '';
  const w = parseFloat(widthM);
  const h = parseFloat(heightM);
  if (w <= 0 || h <= 0) return '';
  return (w * h).toFixed(2);
}

interface BaseDto {
  supplierNumber: string;
  supplierId: number | undefined;
  formatId: number | undefined;
  regionCode: string;
  countyName: string | undefined;
  agglomeration: string | undefined;
  city: string;
  address: string;
  // Absent when the unit's mediaType doesn't require dimensions (CU-12472jzd1eb).
  widthM: number | undefined;
  heightM: number | undefined;
  material: string | null | undefined;
  lighting: string | null | undefined;
  latitude: number | undefined;
  longitude: number | undefined;
  notes: string | undefined;
  wfContactPerson: string | undefined;
  locationDescription: string | undefined;
  catalogPrice: number | undefined;
  rent: number | undefined;
  productionCost: number | undefined;
  installCost: number | undefined;
  dismantlingCost: number | undefined;
  exposureCost: number | undefined;
  additionalCosts: number | undefined;
  discountedPrice: number | undefined;
  lastMinutePrice: number | undefined;
  starterPrice: number | undefined;
  clientProduction: number | undefined;
  clientInstallation: number | undefined;
  clientDismantling: number | undefined;
  clientAdditionalFees: number | undefined;
}

interface CreateDto extends BaseDto {
  variants: Array<{ mediaType: string }>;
}

interface UpdateDto extends Omit<BaseDto, CostFieldKey | 'supplierNumber' | 'widthM' | 'heightM'> {
  deletePhotoIds?: number[];
  // Required for every caller who can see the field (schema's min(1)); a
  // blank value here can only mean the field was disabled for lack of
  // supplier.view, so it means "leave unchanged" (undefined), never "clear"
  // (null) — see buildUpdateDto's supplierNumber override below.
  supplierNumber: string | undefined;
  // A blanked value on a dimension-optional-type unit (e.g. cityscroll) means
  // "clear the stored value" (null), not "leave unchanged" (undefined) — same
  // clearable handling as material/lighting below (CU-12472jzd1eb).
  widthM: number | null | undefined;
  heightM: number | null | undefined;
  catalogPrice: number | null | undefined;
  rent: number | null | undefined;
  productionCost: number | null | undefined;
  installCost: number | null | undefined;
  dismantlingCost: number | null | undefined;
  exposureCost: number | null | undefined;
  additionalCosts: number | null | undefined;
  discountedPrice: number | null | undefined;
  lastMinutePrice: number | null | undefined;
  starterPrice: number | null | undefined;
  clientProduction: number | null | undefined;
  clientInstallation: number | null | undefined;
  clientDismantling: number | null | undefined;
  clientAdditionalFees: number | null | undefined;
}

/**
 * Cost/price fields whose stored value can be cleared from the edit form.
 * On update, an emptied input must send `null` (clear the column) instead of
 * `undefined` (leave unchanged) — mirroring the material/lighting handling below.
 * Sourced from the shared contracts constant; the annotation also asserts every
 * key is a real `FormValues` field at compile time.
 */
const COST_FIELD_KEYS: ReadonlyArray<keyof FormValues & MediaUnitCostFieldKey> =
  MEDIA_UNIT_COST_FIELD_KEYS;

type CostFieldKey = MediaUnitCostFieldKey;

function parseCoordinateNumber(value: string): number | undefined {
  const parsed = parseCoordinateInput(value);
  return parsed === null ? undefined : Number(parsed);
}

// eslint-disable-next-line complexity
function parseBaseDto(values: FormValues): BaseDto {
  return {
    supplierNumber: values.supplierNumber,
    supplierId: values.supplierId ? parseInt(values.supplierId, 10) : undefined,
    formatId: values.formatId ? parseInt(values.formatId, 10) : undefined,
    regionCode: values.regionCode,
    countyName: values.countyName || undefined,
    agglomeration: values.agglomeration || undefined,
    city: values.city,
    address: values.address,
    widthM: values.widthM ? parseFloat(values.widthM) : undefined,
    heightM: values.heightM ? parseFloat(values.heightM) : undefined,
    material: values.material || undefined,
    lighting: values.lighting || undefined,
    latitude: values.latitude ? parseCoordinateNumber(values.latitude) : undefined,
    longitude: values.longitude ? parseCoordinateNumber(values.longitude) : undefined,
    notes: values.notes || undefined,
    wfContactPerson: values.wfContactPerson || undefined,
    locationDescription: values.locationDescription || undefined,
    catalogPrice: values.catalogPrice ? parseFloat(values.catalogPrice) : undefined,
    rent: values.rent ? parseFloat(values.rent) : undefined,
    productionCost: values.productionCost ? parseFloat(values.productionCost) : undefined,
    installCost: values.installCost ? parseFloat(values.installCost) : undefined,
    dismantlingCost: values.dismantlingCost ? parseFloat(values.dismantlingCost) : undefined,
    exposureCost: values.exposureCost ? parseFloat(values.exposureCost) : undefined,
    additionalCosts: values.additionalCosts ? parseFloat(values.additionalCosts) : undefined,
    discountedPrice: values.discountedPrice ? parseFloat(values.discountedPrice) : undefined,
    lastMinutePrice: values.lastMinutePrice ? parseFloat(values.lastMinutePrice) : undefined,
    starterPrice: values.starterPrice ? parseFloat(values.starterPrice) : undefined,
    clientProduction: values.clientProduction ? parseFloat(values.clientProduction) : undefined,
    clientInstallation: values.clientInstallation
      ? parseFloat(values.clientInstallation)
      : undefined,
    clientDismantling: values.clientDismantling ? parseFloat(values.clientDismantling) : undefined,
    clientAdditionalFees: values.clientAdditionalFees
      ? parseFloat(values.clientAdditionalFees)
      : undefined,
  };
}

export function buildCreateDto(values: FormValues): CreateDto {
  return {
    ...parseBaseDto(values),
    // Default variant until variant UI is implemented.
    // The API requires at least one variant (min(1)).
    variants: [{ mediaType: 'billboard' }],
  };
}

export function buildUpdateDto(values: FormValues, deletedPhotoIds: number[]): UpdateDto {
  const base = parseBaseDto(values);
  // When clearing an optional field, the form emits ''. parseBaseDto converts '' to
  // undefined (same as "not provided"), but on an update that would silently skip the
  // field and leave the old DB value in place. Sending null explicitly tells the API
  // to set the column to NULL. This applies to dict fields (material / lighting) and
  // every clearable cost/price field.
  const clearableOverrides: Record<string, number | string | null | undefined> = {
    material: values.material === '' ? null : base.material,
    lighting: values.lighting === '' ? null : base.lighting,
    widthM: values.widthM === '' ? null : base.widthM,
    heightM: values.heightM === '' ? null : base.heightM,
  };
  for (const key of COST_FIELD_KEYS) {
    clearableOverrides[key] = values[key] === '' ? null : base[key];
  }
  const supplierNumber = values.supplierNumber === '' ? undefined : base.supplierNumber;
  if (deletedPhotoIds.length > 0) {
    return { ...base, ...clearableOverrides, supplierNumber, deletePhotoIds: deletedPhotoIds };
  }
  return { ...base, ...clearableOverrides, supplierNumber };
}
